import { useTranslation } from 'react-i18next'
import { useRef } from 'react'
import { Accordion } from 'react-bootstrap'
import { Slider } from 'components/ui/Slider'
import { ICourseCard } from 'models/courseCard.model'
import { IEnrolCard } from 'models/enrolCard.model'
import { CourseVerticalCard } from 'components/ui/CourseCards'
import useSlidesToShow from 'hooks/useSlidesToShow'
import { useIsMobile } from 'hooks/useIsMobile'
import { COURSE_VERTICAL_CARD_SLIDER_MARGIN } from 'components/ui/CourseCards/components/molecules/CourseVerticalCard/constants'
import { useAccess } from 'contexts/AccessContext'
import Loader from 'components/ui/loader/Loader'
import { AccordionToggle } from 'components/ui/AccordionToggle'
import { CategoryHeader } from '../CategoryHeader/CategoryHeader'
import styles from './CourseSection.module.scss'

interface Props {
  title: string
  courses:
    | {
        course: ICourseCard
        enrol: IEnrolCard
      }[]
    | undefined
  totalCourses: number
  href: string
  isLoading?: boolean
}

export const CourseSection: React.FC<Props> = ({
  courses,
  totalCourses,
  href,
  title,
  isLoading,
}) => {
  const { t } = useTranslation()
  const swiping = useRef(false)
  const isMobile = useIsMobile()

  const { isAdmin } = useAccess()

  const sliderContainerRef = useRef<HTMLDivElement>(null)

  const slidesToShow = useSlidesToShow({
    containerRef: sliderContainerRef,
  })

  if (!isLoading && !courses?.length) return null

  return (
    <Accordion defaultActiveKey={title}>
      <AccordionToggle eventKey={title}>
        <CategoryHeader
          title={title}
          count={totalCourses || 0}
          showMoreLink={href}
          showMoreText={t('availableCourses.showMore')}
        />
      </AccordionToggle>
      <Accordion.Collapse eventKey={title}>
        <div>
          {isLoading && <Loader color />}
          {courses?.length ? (
            <div ref={sliderContainerRef} className={styles.sliderContainer}>
              <Slider
                afterChange={() => {
                  swiping.current = false
                }}
                beforeChange={() => {
                  swiping.current = true
                }}
                dots={false}
                slidesToScroll={1}
                centerMode={false}
                variableWidth
                arrows={!isMobile && courses.length > slidesToShow}
                infinite={courses.length > slidesToShow}
                swipe={courses.length > slidesToShow}
              >
                {courses.map((attributes) => (
                  <div key={attributes.course.id}>
                    {/* Is necessary to wrap the card with a div to avoid a bug with the margin */}
                    <div
                      style={{
                        marginRight: COURSE_VERTICAL_CARD_SLIDER_MARGIN,
                      }}
                    >
                      <CourseVerticalCard
                        course={attributes.course}
                        enrol={null}
                        enrolled={!isAdmin()}
                        swiping={() => swiping.current}
                      />
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
          ) : null}
        </div>
      </Accordion.Collapse>
    </Accordion>
  )
}
